import {
  Avatar,
  Badge,
  Card,
  Text,
  type AvatarProps,
  type BadgeProps,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { useAtom } from "jotai";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";

import personasData from "@/data/personas.json";
import {
  personaAtom,
  preferencesStoreAtom,
  buildDefaults,
  ROLE_DEFAULT_ROUTES,
  type PersonaRole,
} from "@/store";
import { isValidRouteForRole } from "@/config/routes";
import type { UserPersona } from "@/types";

type PersonaFixture = Omit<UserPersona, "avatarUrl"> & {
  avatarUrl: string | null;
};

const ROLE_BADGE_COLORS: Record<string, BadgeProps["color"]> = {
  "compliance-officer": "brand",
  investigator: "danger",
  "revenue-recovery": "warning",
  "intelligence-analyst": "informative",
  "team-leader": "success",
  "executive-director": "subtle",
};

const ROLE_AVATAR_COLORS: Record<string, AvatarProps["color"]> = {
  "compliance-officer": "brand",
  investigator: "colorful",
  "revenue-recovery": "colorful",
  "intelligence-analyst": "colorful",
  "team-leader": "colorful",
  "executive-director": "neutral",
};

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: tokens.spacingHorizontalM,
  },
  card: {
    cursor: "pointer",
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingInlineStart: tokens.spacingHorizontalM,
    paddingInlineEnd: tokens.spacingHorizontalM,
    transitionDuration: "150ms",
    transitionProperty: "box-shadow, border-color",
  },
  selected: {
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    boxShadow: tokens.shadow8Brand,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
  },
  roleLabel: {
    color: tokens.colorNeutralForeground3,
  },
  team: {
    color: tokens.colorNeutralForeground4,
  },
});

interface PersonaSwitcherProps {
  onSelect?: () => void;
}

export function PersonaSwitcher({ onSelect }: PersonaSwitcherProps = {}) {
  const [persona, setPersona] = useAtom(personaAtom);
  const [preferencesStore, setPreferencesStore] = useAtom(preferencesStoreAtom);
  const preferencesStoreRef = useRef(preferencesStore);
  preferencesStoreRef.current = preferencesStore;
  const navigate = useNavigate();
  const styles = useStyles();
  const personas = (personasData as PersonaFixture[]).map(
    ({ avatarUrl: _avatarUrl, ...selectedPersona }) => selectedPersona,
  );

  const handleSelect = (selectedPersona: UserPersona) => {
    const role = selectedPersona.role as PersonaRole;
    setPersona({
      userId: selectedPersona.id,
      name: selectedPersona.name,
      role,
      roleLabel: selectedPersona.roleLabel,
      density: selectedPersona.preferences.density,
      teamId: selectedPersona.teamId,
      teamName: selectedPersona.teamName,
      permissions: [],
    });

    // Ensure preferences exist for this persona, seeding from fixture data
    const store = preferencesStoreRef.current;
    const stored = store.data[selectedPersona.id];
    if (!stored) {
      const defaults = buildDefaults(
        selectedPersona.id,
        role,
        selectedPersona.preferences.darkMode,
      );
      setPreferencesStore({
        ...store,
        data: { ...store.data, [selectedPersona.id]: defaults },
      });
    }

    // Navigate to stored default route or role-based fallback
    const prefs = store.data[selectedPersona.id];
    const target =
      prefs && isValidRouteForRole(prefs.defaultRoute, role)
        ? prefs.defaultRoute
        : (ROLE_DEFAULT_ROUTES[role] ?? "/home");
    navigate(target);
    onSelect?.();
  };

  return (
    <div className={styles.grid}>
      {personas.map((selectedPersona) => (
        <Card
          key={selectedPersona.id}
          className={mergeClasses(
            styles.card,
            persona.userId === selectedPersona.id && styles.selected,
          )}
          onClick={() => handleSelect(selectedPersona)}
          role="button"
        >
          <div className={styles.header}>
            <Avatar
              name={selectedPersona.name}
              size={32}
              color={ROLE_AVATAR_COLORS[selectedPersona.role] ?? "brand"}
            />
            <div>
              <Text weight="semibold" size={300} block>
                {selectedPersona.name}
              </Text>
              <Text size={200} className={styles.roleLabel}>
                {selectedPersona.roleLabel}
              </Text>
            </div>
          </div>
          <Text size={200} className={styles.team}>
            {selectedPersona.teamName}
          </Text>
          <Badge
            appearance="tint"
            color={ROLE_BADGE_COLORS[selectedPersona.role] ?? "brand"}
            size="small"
            style={{ marginTop: "4px" }}
          >
            {selectedPersona.role.replace(/-/g, " ")}
          </Badge>
        </Card>
      ))}
    </div>
  );
}
